import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import { detectUserPrefix } from '../components/detectUserPrefix';
import { BookOpen, Mail, Lock, User, Phone, Eye, EyeOff } from 'lucide-react';
import { BillingFieldsSection } from '../components/BillingFieldsSection';
import { toast } from 'sonner';
import axios from 'axios';

export const LoginPage = () => {
  const { t } = useTranslation();
  const { login, verify2FA, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  // 2FA step state: when the backend says ``requires_2fa`` we switch to
  // a code-entry view that posts to /auth/2fa/verify with the pending
  // token returned by /auth/login.
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password, rememberMe);
      if (result?.requires_2fa) {
        setPendingToken(result.pending_token);
        toast.info('Introduce el código de tu app autenticadora.');
      } else {
        // Skip the welcome toast for admins — they log in often and the
        // "¡Bienvenido!" notification is noise for them.
        if (!result?.is_admin) {
          toast.success('¡Bienvenido!');
        }
        navigate('/dashboard');
      }
    } catch (error) {
      const raw = error.response?.data?.detail;
      const status = error.response?.status;
      const translations = {
        'Invalid credentials': 'Email o contraseña incorrectos.',
      };
      const msg = (typeof raw === 'string' && translations[raw]) ? translations[raw] : raw;
      // Friendlier copy for rate-limit (429) so the user understands what to do.
      if (status === 429) {
        toast.error(msg || 'Demasiados intentos. Espera unos minutos antes de volver a intentarlo.');
      } else {
        toast.error(msg || 'No se pudo iniciar sesión. Comprueba tus credenciales.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await verify2FA(pendingToken, code);
      if (!result?.is_admin) {
        toast.success('¡Bienvenido!');
      }
      if (result?.used_backup_code) {
        toast.warning(`Has usado un código de respaldo. Te quedan ${result.backup_codes_remaining}.`, { duration: 8000 });
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código incorrecto');
      if (error.response?.status === 401) {
        // Pending token expired — back to the password step.
        setPendingToken(null);
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="card-elevated" data-testid="login-card">
            <CardHeader className="text-center pb-2">
              <div
                className="mx-auto w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4 shadow-sm"
                aria-hidden="true"
              >
                <BookOpen className="w-6 h-6" style={{ color: '#FFCC00' }} strokeWidth={1.8} />
              </div>
              <CardTitle className="font-heading text-2xl">{t('auth.login')}</CardTitle>
              <CardDescription>{t('hero.subtitle').slice(0, 50)}...</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {pendingToken ? (
                <form onSubmit={handle2FASubmit} className="space-y-4" data-testid="login-2fa-form">
                  <div className="text-center mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                    <p className="text-sm font-medium text-emerald-900">Verificación en 2 pasos</p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Abre tu app autenticadora (Google Authenticator, Authy, 1Password…) y escribe el código de 6 dígitos. Si no tienes acceso, usa uno de tus códigos de respaldo.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="totp-code" className="input-label">
                      <Lock className="w-4 h-4 inline mr-2" />
                      Código de verificación
                    </Label>
                    <Input
                      id="totp-code"
                      type="text"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      className="input-field tracking-[0.5em] text-center text-lg font-mono"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="123456"
                      required
                      autoFocus
                      data-testid="login-2fa-code"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading || code.length < 6}
                    data-testid="login-2fa-submit"
                  >
                    {loading ? t('common.loading') : 'Verificar e iniciar sesión'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setPendingToken(null); setCode(''); }}
                    className="block w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="login-2fa-cancel"
                  >
                    ← Volver e introducir email/contraseña de nuevo
                  </button>
                </form>
              ) : (
              <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="input-label">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="input-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    {t('auth.password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="input-field pr-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="login-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="remember-me" 
                      checked={rememberMe} 
                      onCheckedChange={setRememberMe}
                      data-testid="login-remember-me"
                    />
                    <Label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
                      {t('auth.rememberMe')}
                    </Label>
                  </div>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? t('common.loading') : t('auth.login')}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-muted-foreground">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loginWithGoogle}
                data-testid="google-login"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.google')}
              </Button>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {t('auth.noAccount')}{' '}
                <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                  {t('auth.register')}
                </Link>
              </p>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { t } = useTranslation();
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get('email') || '';
  const pendingCoupon = searchParams.get('coupon') || '';
  // Beta-mode flag from /api/public-config — when true, the register
  // form gates registration behind a valid access coupon code.
  const [betaMode, setBetaMode] = useState(false);
  // The registration coupon: prefilled from ?beta= or ?coupon= URL params
  // so the live price preview reflects it immediately.
  const [betaCode, setBetaCode] = useState((searchParams.get('beta') || searchParams.get('coupon') || '').toUpperCase());
  const [couponInfo, setCouponInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: prefilledEmail, 
    phone: '',
    phone_prefix: '+34',
    password: '',
    taxId: '',
    fiscalName: '',
    billingType: 'particular',
    address: '',
    postalCode: '',
    city: '',
    province: ''
  });
  const [prefixAutoSet, setPrefixAutoSet] = useState(false);

  useEffect(() => {
    // Detect beta-mode flag once on mount.
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public-config`)
      .then(res => setBetaMode(!!res.data?.beta_mode))
      .catch(() => setBetaMode(false));
  }, []);

  useEffect(() => {
    // Auto-detect user's country prefix (browser language → IP geo).
    // Only applies if the user hasn't manually picked a prefix yet.
    let cancelled = false;
    detectUserPrefix().then(prefix => {
      if (!cancelled && prefix && !prefixAutoSet) {
        setFormData(d => d.phone_prefix === '+34' ? { ...d, phone_prefix: prefix } : d);
        setPrefixAutoSet(true);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingCoupon) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/${pendingCoupon}/preview`);
        if (!cancelled) setCouponInfo(res.data);
      } catch {
        if (!cancelled) setCouponInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [pendingCoupon]);

  // Live price preview for the pay-at-signup model (coupon aware).
  const [regQuote, setRegQuote] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const code = betaCode.trim();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/registration-quote${code ? `?coupon_code=${encodeURIComponent(code)}` : ''}`;
        const res = await axios.get(url);
        if (!cancelled) setRegQuote(res.data);
      } catch {
        if (!cancelled) setRegQuote(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [betaCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (betaMode && !betaCode.trim()) {
      toast.error('Necesitas un cupón para registrarte. Si no tienes uno, únete a la lista de espera.');
      return;
    }
    setLoading(true);
    try {
      const fullPhone = formData.phone ? `${formData.phone_prefix} ${formData.phone}` : '';
      const billing = {
        billing_type: formData.billingType,
        tax_id: formData.taxId.trim(),
        fiscal_name: formData.billingType === 'empresa' ? formData.fiscalName.trim() : '',
        address: formData.address.trim(),
        postal_code: formData.postalCode.trim(),
        city: formData.city.trim(),
        province: formData.province.trim(),
      };
      const res = await register(formData.email, formData.password, formData.firstName, formData.lastName, fullPhone, betaCode.trim().toUpperCase(), billing);
      if (res?.payment_pending && res?.checkout_url) {
        toast.success('¡Cuenta creada! Te llevamos al pago seguro…');
        window.location.href = res.checkout_url;
        return;
      }
      toast.success('¡Cuenta creada con éxito!');
      navigate('/dashboard');
    } catch (error) {
      // Map common backend messages to friendlier Spanish text so the user
      // knows exactly what to fix (e.g. duplicate email, invalid coupon).
      // FastAPI may return the detail as a string OR a list of validation
      // errors — normalise both shapes before showing the toast.
      const raw = error.response?.data?.detail;
      let msg;
      if (Array.isArray(raw)) {
        msg = raw.map((d) => d.msg || JSON.stringify(d)).join(' · ');
      } else if (typeof raw === 'string') {
        msg = raw;
      }
      const translations = {
        'Email already registered': 'Este email ya está registrado. Inicia sesión o usa otro distinto.',
        'Invalid credentials': 'Email o contraseña incorrectos.',
        'Code already used': 'Este cupón ya ha sido usado.',
      };
      if (msg && translations[msg]) msg = translations[msg];
      toast.error(msg || 'No se pudo completar el registro. Inténtalo de nuevo en unos minutos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="card-elevated" data-testid="register-card">
            <CardHeader className="text-center pb-2">
              <div
                className="mx-auto w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4 shadow-sm"
                aria-hidden="true"
              >
                <BookOpen className="w-6 h-6" style={{ color: '#FFCC00' }} strokeWidth={1.8} />
              </div>
              <CardTitle className="font-heading text-2xl">{t('auth.register')}</CardTitle>
              <CardDescription>{t('hero.subtitle').slice(0, 50)}...</CardDescription>
              {pendingCoupon && (
                <div
                  className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-left"
                  data-testid="coupon-prompt-banner"
                >
                  <p className="text-sm font-semibold text-amber-900">
                    🎁 Tienes un cupón de regalo: <span className="font-mono">{pendingCoupon}</span>
                  </p>
                  {couponInfo && couponInfo.type === 'percentage' && (
                    <p className="mt-1 text-xs text-amber-900">
                      <strong>{couponInfo.value}% de descuento</strong> en tu compra.
                    </p>
                  )}
                  {couponInfo && couponInfo.type === 'fixed' && (
                    <p className="mt-1 text-xs text-amber-900">
                      <strong>{couponInfo.value}€ de descuento</strong> en tu compra.
                    </p>
                  )}
                  <p className="mt-1 text-xs text-amber-800 leading-relaxed">
                    Se aplicará automáticamente al total de tu compra al registrarte — verás el descuento reflejado en el precio.
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-6">
              {betaMode && (
                <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50/60" data-testid="beta-mode-banner">
                  <p className="text-xs text-amber-900 font-medium mb-2">
                    🔒 Estamos en fase piloto privada
                  </p>
                  <Label htmlFor="betaCode" className="input-label text-xs text-amber-900">
                    Cupón
                  </Label>
                  <Input
                    id="betaCode"
                    type="text"
                    className="input-field font-mono uppercase tracking-wide"
                    value={betaCode}
                    onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                    placeholder="EJ: PILOTOMANOLO"
                    required
                    autoComplete="off"
                    data-testid="beta-code-input"
                  />
                  <p className="text-[11px] text-amber-800 mt-1.5">
                    ¿No tienes código?{' '}
                    <Link to="/lista-de-espera" className="font-semibold underline hover:text-amber-700" data-testid="beta-waitlist-link">
                      Únete a la lista de espera
                    </Link>{' '}
                    y te avisaremos cuando la app esté disponible.
                  </p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                {regQuote && (
                  <div className="p-3 rounded-md border border-teal-200 bg-teal-50/60" data-testid="registration-price-box">
                    <p className="text-xs font-semibold text-teal-900 mb-1.5">Tu libro personalizado</p>
                    <div className="space-y-0.5 text-xs text-teal-900">
                      <div className="flex justify-between"><span>Libro base (1 ejemplar, envío España incluido)</span><span>{regQuote.base_price_eur.toFixed(2)}€</span></div>
                      {regQuote.discount_eur > 0 && (
                        <div className="flex justify-between text-emerald-700 font-medium"><span>{regQuote.discount_description || 'Descuento'}</span><span>−{regQuote.discount_eur.toFixed(2)}€</span></div>
                      )}
                      <div className="flex justify-between"><span>IVA (21%)</span><span>{regQuote.tax_eur.toFixed(2)}€</span></div>
                      <div className="flex justify-between border-t border-teal-200 pt-1 mt-1 text-sm font-bold"><span>Total al registrarte</span><span data-testid="registration-total">{regQuote.total_eur.toFixed(2)}€</span></div>
                    </div>
                    {regQuote.coupon_valid === false && (
                      <p className="text-[11px] text-red-600 mt-1.5 font-medium" data-testid="coupon-invalid-msg">
                        ⚠️ {regQuote.coupon_error || 'El cupón no es válido.'} El precio mostrado no incluye descuento.
                      </p>
                    )}
                    {regQuote.total_eur === 0 ? (
                      <p className="text-[11px] text-emerald-700 mt-1.5 font-medium">🎉 Con tu cupón, el registro es gratuito — sin pago.</p>
                    ) : (
                      <p className="text-[11px] text-teal-800 mt-1.5">Pagarás de forma segura con tarjeta (Stripe) al crear la cuenta. Los ejemplares adicionales se pagan al confirmar el pedido.</p>
                    )}
                  </div>
                )}
                {!betaMode && (
                  <div>
                    <Label htmlFor="couponCode" className="input-label">¿Tienes un cupón? <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                    <Input
                      id="couponCode"
                      type="text"
                      className="input-field font-mono uppercase tracking-wide"
                      value={betaCode}
                      onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                      placeholder="Ej: BIENVENIDO20"
                      autoComplete="off"
                      data-testid="register-coupon-input"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="input-label">
                      <User className="w-4 h-4 inline mr-2" />
                      {t('auth.firstName')}
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      className="input-field"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Ejem: Juan"
                      required
                      data-testid="register-firstname"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="input-label">
                      {t('auth.lastName')}
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      className="input-field"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Ejem: García"
                      required
                      data-testid="register-lastname"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="input-label">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Ejem: ejemplo@email.com"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="input-label">
                    <Phone className="w-4 h-4 inline mr-2" />
                    {t('auth.phone')}
                  </Label>
                  <div className="flex gap-2">
                    <PhonePrefixSelect
                      value={formData.phone_prefix || '+34'}
                      onValueChange={(v) => setFormData({ ...formData, phone_prefix: v })}
                      testId="register-phone-prefix"
                    />
                    <Input
                      id="phone"
                      type="tel"
                      className="input-field flex-1"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="612 345 678"
                      required
                      data-testid="register-phone"
                    />
                  </div>
                </div>
                <BillingFieldsSection formData={formData} setFormData={setFormData} />
                <div>
                  <Label htmlFor="password" className="input-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    {t('auth.password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="input-field pr-10"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="register-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? t('common.loading') : t('auth.register')}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-muted-foreground">{t('auth.orContinueWith')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loginWithGoogle}
                data-testid="google-register"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.google')}
              </Button>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {t('auth.hasAccount')}{' '}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                  {t('auth.login')}
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="card-elevated" data-testid="forgot-password-card">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="font-heading text-2xl">{t('auth.resetPassword')}</CardTitle>
              <CardDescription>{t('auth.resetDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {sent ? (
                <div className="text-center space-y-4" data-testid="reset-sent-message">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <Mail className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="font-medium">{t('auth.resetSent')}</p>
                  <p className="text-sm text-muted-foreground">{t('auth.resetSentDesc')}</p>
                  {resetToken && (
                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">{t('auth.resetLinkPreview')}</p>
                      <Link 
                        to={`/reset-password?token=${resetToken}`}
                        className="text-sm text-primary hover:underline break-all"
                        data-testid="reset-link"
                      >
                        {t('auth.clickToReset')}
                      </Link>
                    </div>
                  )}
                  <Link to="/login">
                    <Button variant="outline" className="mt-4" data-testid="back-to-login">
                      {t('auth.backToLogin')}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-email" className="input-label">
                      <Mail className="w-4 h-4 inline mr-2" />
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="reset-email"
                      type="email"
                      className="input-field"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Ejem: ejemplo@email.com"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? t('common.loading') : t('auth.sendResetLink')}
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    <Link to="/login" className="text-primary hover:underline">
                      {t('auth.backToLogin')}
                    </Link>
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenEmail, setTokenEmail] = useState('');

  const token = new URLSearchParams(window.location.search).get('token');

  useState(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`)
      .then(res => {
        setTokenValid(true);
        setTokenEmail(res.data.email);
      })
      .catch(() => setTokenValid(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      toast.success(t('auth.passwordResetSuccess'));
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetError'));
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-md mx-auto text-center">
            <Card className="card-elevated" data-testid="invalid-token-card">
              <CardContent className="py-12">
                <Lock className="w-12 h-12 mx-auto text-red-400 mb-4" />
                <h2 className="font-heading text-xl font-semibold mb-2">{t('auth.invalidToken')}</h2>
                <p className="text-muted-foreground mb-6">{t('auth.invalidTokenDesc')}</p>
                <Link to="/forgot-password">
                  <Button className="btn-primary">{t('auth.requestNewLink')}</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="card-elevated" data-testid="reset-password-card">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="font-heading text-2xl">{t('auth.newPassword')}</CardTitle>
              {tokenEmail && (
                <CardDescription>{tokenEmail}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="input-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    {t('auth.newPassword')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      className="input-field pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="reset-toggle-password"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="input-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    {t('auth.confirmPassword')}
                  </Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    className="input-field"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="reset-confirm-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? t('common.loading') : t('auth.changePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
